import { jsxs, jsx } from "react/jsx-runtime";
import { forceString } from "@flatfile/design-system";
import { useRef, useState, useCallback, useEffect, createElement, Fragment } from "react";
import { Container, StyledEditorValueClone, StyledEditorInput } from "./Styles.js";
const NON_BREAKING_SPACE = " ";
const getInitValue = (keyPressValue, value) => {
  if (keyPressValue)
    return keyPressValue;
  return forceString(value);
};
const valueToHTML = (value) => {
  return value.split("\n").reduce((memo, text, index, arr) => {
    memo.push(text.replace(/\s/g, NON_BREAKING_SPACE));
    if (index !== arr.length - 1) {
      memo.push(/* @__PURE__ */ jsx("br", {}));
    }
    return memo;
  }, []);
};
const Editor = ({
  editorRef,
  data,
  keyPressValue,
  warning,
  error,
  hasChanged,
  mutated,
  number,
  setCurrentValue,
  isFirstNonHeaderRow,
  isLastRow,
  isLastColumn,
  isFirstColumn
}) => {
  const cloneRef = useRef(null);
  const initValue = getInitValue(keyPressValue, data.value);
  const [defaultValue, setDefaultValue] = useState(initValue);
  const [htmlDefaultValue, setHTMLDefaultValue] = useState(
    valueToHTML(initValue)
  );
  const [cloneMinWidth, setCloneMinWidth] = useState("0px");
  const [width, setWidth] = useState("0px");
  const [height, setHeight] = useState("0px");
  const setRef = useCallback((node) => {
    editorRef.current = node;
    if (node && node.parentElement) {
      const computedStyle = window.getComputedStyle(node.parentElement, null);
      setCloneMinWidth(computedStyle.getPropertyValue("width"));
    }
  }, []);
  const handleChange = (event) => {
    const newValue = event.target.value;
    const newHTMLDefaultValue = valueToHTML(newValue);
    setHTMLDefaultValue(newHTMLDefaultValue);
    setDefaultValue(newValue);
    setCurrentValue(newValue);
  };
  const handleKeydown = (event) => {
    if (event.key === "Enter" && event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target;
      const cursorPosition = target.selectionStart;
      const textBeforeCursor = target.value.substring(0, cursorPosition);
      const textAfterCursor = target.value.substring(cursorPosition);
      const newText = `${textBeforeCursor}
${textAfterCursor}`;
      setHTMLDefaultValue(valueToHTML(newText));
      target.value = newText;
      target.selectionStart = cursorPosition + 1;
      target.selectionEnd = cursorPosition + 1;
      setDefaultValue(newText);
      setCurrentValue(newText);
    }
  };
  useEffect(() => {
    if (keyPressValue) {
      setCurrentValue(initValue);
    }
  }, [keyPressValue]);
  useEffect(() => {
    if (editorRef.current && cloneRef.current) {
      var computedStyle = window.getComputedStyle(cloneRef.current, null);
      setWidth(computedStyle.getPropertyValue("width"));
      setHeight(computedStyle.getPropertyValue("height"));
    }
  }, [htmlDefaultValue]);
  return /* @__PURE__ */ jsxs(
    Container,
    {
      error: !!error,
      warning: !!warning,
      isLastRow,
      isFirstColumn,
      isLastColumn,
      isFirstNonHeaderRow,
      noShadow: true,
      children: [
        /* @__PURE__ */ jsxs(
          StyledEditorValueClone,
          {
            ref: cloneRef,
            style: {
              minWidth: cloneMinWidth
            },
            isFirstNonHeaderRow,
            isFirstColumn,
            children: [
              createElement(Fragment, {}, ...htmlDefaultValue),
              " "
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          StyledEditorInput,
          {
            ref: setRef,
            role: "editor",
            isUnderline: mutated && !hasChanged,
            number,
            value: defaultValue,
            onChange: handleChange,
            onKeyDown: handleKeydown,
            onFocus: (e) => {
              if (!e.target.dataset.focused) {
                e.target.dataset.focused = "true";
                e.target.setSelectionRange(
                  e.target.value.length,
                  e.target.value.length
                );
              }
            },
            onBlur: (e) => {
              e.target.dataset.focused = "false";
            },
            style: { width, height },
            isFirstNonHeaderRow,
            isFirstColumn
          }
        )
      ]
    }
  );
};
export {
  Editor as default
};
